import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useBlocker } from "react-router-dom";
import { reduxForm, getFormValues, getFormSyncErrors, getFormSubmitErrors, hasSubmitFailed, change, reset, initialize, touch } from "redux-form";
import { ROLES, PERMISSION_PREFIXES, VALIDATION_FIELD_PREFIXES, USER_FORM_NAME } from "../constants";
import { resolveRoleCaseInsensitive } from "../utils/roleHelpers";
import "../../FormComponent/tags_style.css";
import LoaderComponent from "../../../Loader";
import { validateAddUserForm, validateEditUserForm } from "../utils/formValidators";
import { getAllPermissions } from "../config/permissions.config";
import { ROLE_PRESETS, resolveRolePresets } from "../config/roles.config";
import { RULES } from "../config/rules.config";
import { computePermissionState } from "../utils/permissionEngine";
import { getAllPermissionKeysFromTree } from "../utils/cascadeHelpers";
import { resolveRules } from "../utils/ruleResolver";
import { applyRoleRuleOverrides } from "../config/rules.roleOverrides";
import {
  filterPermissionTree,
  getHiddenPermissionKeySet,
  getAvailablePermissionKeySet,
} from "../utils/moduleGating";
import {
  selectCurrentUser,
  selectCurrentUserLoading,
  selectTemplates,
  selectActionStatus,
  selectActionErrors,
  selectSuccessMessage,
  selectUserDetail,
  selectUserDetailLoading,
  selectUsernameAvailability,
  selectUsernameChecking,
  selectUsernameError,
  selectSupervisorsList,
  selectSupervisorsLoading,
  selectSupervisorsPage,
  selectSupervisorsHasMore,
} from "_admin/features/users/usersSelectors";
import {
  createUserRequest,
  updateUserRequest,
  fetchUserDetailRequest,
  clearSuccessMessage,
  clearError,
  fetchSupervisorsListRequest,
  resetSupervisorsList,
  fetchTemplatesRequest,
  clearUserDetail,
} from "_admin/features/users/usersSlice";
import { buildCreateUserPayload, buildUpdateUserPayload } from "_admin/features/users/UserService";
import { fetchUser as fetchUserApi } from "_admin/features/users/usersAPI";
import { showToast } from '@container/SpockViewPage/actions/action';
import DialogueBox from '../../../DialogueBox';
import ConfirmationPrompt from '../../../ConfirmationPrompt';
import BasicInfo from "./BasicInfo";
import PermissionSection from "./PermissionSection";
import { useModules } from '../../../../utils/modules';

const FORM_NAME = USER_FORM_NAME;

const setsAreEqual = (a, b) => {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
};

const isPermissionKey = (key) =>
  key.startsWith(PERMISSION_PREFIXES.CHECKBOX) ||
  key.startsWith(PERMISSION_PREFIXES.CAN_LOWER) ||
  key.startsWith(PERMISSION_PREFIXES.CAN_UPPER) ||
  key.startsWith(PERMISSION_PREFIXES.STATE) ||
  key.startsWith(PERMISSION_PREFIXES.TEMPLATE);

const normalizeFormValues = (values, { excludePermissions = false } = {}) => {
  if (!values) return '{}';
  const normalized = {};
  Object.keys(values).sort().forEach(key => {
    if (excludePermissions && isPermissionKey(key)) return;
    if (excludePermissions && key === 'role') return;
    const val = values[key];
    if (val != null && val !== '' && val !== false) {
      normalized[key] = val;
    }
  });
  return JSON.stringify(normalized);
};

const scrollToFirstError = (root, errorKeys) => {
  if (!root) return;
  let candidates = Array.from(root.querySelectorAll('.errorMsg')).filter((el) => el.offsetParent !== null);
  if (candidates.length === 0) {
    candidates = errorKeys
      .map((key) => root.querySelector(`[name="${key}"], [id="${key}"]`))
      .filter((el) => el && el.offsetParent !== null);
  }
  if (candidates.length === 0) return;
  candidates.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  const first = candidates[0];
  const fieldSelector = 'input, select, textarea';
  let fieldContainer = first;
  while (fieldContainer && fieldContainer !== root && !fieldContainer.matches(fieldSelector) && !fieldContainer.querySelector(fieldSelector)) {
    fieldContainer = fieldContainer.parentElement;
  }
  const hasField = fieldContainer && fieldContainer !== root;
  const scrollTarget = hasField ? fieldContainer : first;
  scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (hasField) {
    const focusable = fieldContainer.matches(fieldSelector) ? fieldContainer : fieldContainer.querySelector(fieldSelector);
    if (focusable) focusable.focus({ preventScroll: true });
  }
};

const AddUser = ({
  isEditMode = false,
  userId = null,
  onClose = () => {},
  handleSubmit,
  submitting,
  invalid,
  dispatch,
}) => {
  const reduxDispatch = useDispatch();
  const { isModuleEnabled, domain } = useModules();
  const [selectedRole, setSelectedRole] = useState(ROLES.SUPERVISOR);
  const bypassBlockRef = useRef(false);
  const hiddenPermissionKeysRef = useRef(new Set());

  const currentUser = useSelector(selectCurrentUser);
  const currentUserLoading = useSelector(selectCurrentUserLoading);
  const templates = useSelector(selectTemplates);
  const actionStatus = useSelector(selectActionStatus);
  const actionErrors = useSelector(selectActionErrors);
  const successMessage = useSelector(selectSuccessMessage);
  const formValues = useSelector(getFormValues(FORM_NAME)) || {};
  const formSyncErrors = useSelector(getFormSyncErrors(FORM_NAME)) || {};
  const formSubmitErrors = useSelector(getFormSubmitErrors(FORM_NAME)) || {};
  const submitFailed = useSelector(hasSubmitFailed(FORM_NAME));
  const userDetail = useSelector(selectUserDetail);
  const userDetailLoading = useSelector(selectUserDetailLoading);
  const usernameAvailable = useSelector(selectUsernameAvailability);
  const usernameChecking = useSelector(selectUsernameChecking);
  const usernameError = useSelector(selectUsernameError);
  const supervisorsList = useSelector(selectSupervisorsList);
  const supervisorsLoading = useSelector(selectSupervisorsLoading);
  const supervisorsPage = useSelector(selectSupervisorsPage);
  const supervisorsHasMore = useSelector(selectSupervisorsHasMore);

  // Permission system state
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [derivedState, setDerivedState] = useState({
    lockedPermissions: new Set(),
    disabledPermissions: new Set(),
  });

  // Refs
  const roleChangeRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const prevFingerprintRef = useRef("");
  const permissionsBaselineRef = useRef("");
  const lockedPermissionsRef = useRef(new Set());
  const disabledPermissionsRef = useRef(new Set());
  const hasAppliedDefaults = useRef(false);
  const templatesAppliedRef = useRef(false);
  const thresholdParentStatesRef = useRef({});
  const submitIntentRef = useRef(null);
  const [activeAction, setActiveAction] = useState(null);
  const [errorScrollTick, setErrorScrollTick] = useState(0);
  const rootRef = useRef(null);
  const errorKeysRef = useRef([]);
  const requestErrorScroll = useCallback(() => setErrorScrollTick((tick) => tick + 1), []);
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;
  // In edit mode: snapshot of the initially fetched user detail. Used to
  // restore the exact saved permissions/templates when the user switches
  // back to the original role after having explored a different one.
  const savedUserDetailRef = useRef(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [initialFieldsSnapshot, setInitialFieldsSnapshot] = useState(null);

  // Resolved supervisor for edit mode: fetched by supervisor_id so it can be
  // pre-selected even when it is not in the currently loaded supervisors page.
  const [resolvedSupervisor, setResolvedSupervisor] = useState(null);
  const supervisorSearchRef = useRef("");

  // Supervisor options - filter out blocked and temporary users
  const supervisorOptions = useMemo(() => {
    const options = supervisorsList
      .filter(user => !user.isBlocked && !user.isTempUser)
      .filter(user => !isEditMode || String(user.id) !== String(userId))
      .map(user => {
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName = fullName || user.username || user.email || `User #${user.id}`;
        
        // FormSelectSearch expects Value and DisplayName (capital letters)
        return {
          Value: user.id,
          DisplayName: displayName,
        };
      });

    // Prepend the resolved supervisor (from userDetail.supervisor_id) if it
    // isn't already present in the loaded page. This guarantees the saved
    if (
      resolvedSupervisor &&
      !supervisorSearchRef.current &&
      !options.some((o) => o.Value === resolvedSupervisor.Value)
    ) {
      return [resolvedSupervisor, ...options];
    }
    return options;
  }, [supervisorsList, resolvedSupervisor, isEditMode, userId]);

  // Fetch first page of supervisors on mount, and reset on unmount
  useEffect(() => {
    reduxDispatch(fetchSupervisorsListRequest({ page: 1, limit: 100 }));
    return () => {
      reduxDispatch(resetSupervisorsList());
    };
  }, [reduxDispatch]);

  // Templates power the "Create Workkard"/"Create Activity" template
  // checkboxes (e.g. Claim/Commercial Workkard, ALE - Loss of Rents, etc.).
  // Without this, `templates` stays empty and those checkboxes never render,
  // so autoCheckTemplates on a role preset would have nothing to check.
  useEffect(() => {
    reduxDispatch(fetchTemplatesRequest());
  }, [reduxDispatch]);

  const supervisorLoadingRef = useRef(false);
  const supervisorsPageRef = useRef(1);
  const supervisorsHasMoreRef = useRef(true);
  useEffect(() => {
    supervisorLoadingRef.current = supervisorsLoading;
  }, [supervisorsLoading]);
  useEffect(() => {
    supervisorsPageRef.current = supervisorsPage;
  }, [supervisorsPage]);
  useEffect(() => {
    supervisorsHasMoreRef.current = supervisorsHasMore;
  }, [supervisorsHasMore]);

  const handleSupervisorScrollEnd = useCallback(() => {
    if (supervisorsHasMoreRef.current && !supervisorLoadingRef.current) {
      supervisorLoadingRef.current = true;
      const nextPage = supervisorsPageRef.current + 1;
      reduxDispatch(fetchSupervisorsListRequest({
        page: nextPage,
        limit: 100,
        search_string: supervisorSearchRef.current,
      }));
    }
  }, [reduxDispatch]);

  const handleSupervisorSearch = useCallback((searchTerm) => {
    supervisorSearchRef.current = searchTerm || "";
    reduxDispatch(fetchSupervisorsListRequest({
      page: 1,
      limit: 100,
      search_string: supervisorSearchRef.current,
    }));
  }, [reduxDispatch]);

  // Clear stale errors/success messages when form mounts
  useEffect(() => {
    reduxDispatch(clearError());
    reduxDispatch(clearSuccessMessage());
  }, [reduxDispatch]);

  // Track previous loading state to detect action completion transitions
  const prevCreatingRef = useRef(false);
  const prevUpdatingRef = useRef(false);

  // Handle action completion (success / error toast + post-save flow)
  useEffect(() => {
    const wasCreating = prevCreatingRef.current;
    const wasUpdating = prevUpdatingRef.current;
    prevCreatingRef.current = actionStatus.creating;
    prevUpdatingRef.current = actionStatus.updating;

    const justFinished =
      (wasCreating && !actionStatus.creating) ||
      (wasUpdating && !actionStatus.updating);

    if (!justFinished) return;
    setActiveAction(null);
    if (!submitIntentRef.current) return;

    const intent = submitIntentRef.current;
    submitIntentRef.current = null;

    const errorMsg = actionErrors.create || actionErrors.update;

    if (errorMsg) {
      reduxDispatch(showToast({
        message: errorMsg,
        type: 'warning',
        iconType: 'warning',
        autoClose: true,
        delayTime: 5000,
        showActions: false,
      }));
      if (errorKeysRef.current.length > 0) dispatch(touch(FORM_NAME, ...errorKeysRef.current));
      requestErrorScroll();
      return;
    }

    // Success path
    reduxDispatch(showToast({
      message: successMessage || (isEditMode ? 'User updated successfully' : 'User created successfully'),
      type: 'success',
      iconType: 'success',
      autoClose: true,
      delayTime: 3000,
      showActions: false,
    }));
    reduxDispatch(clearSuccessMessage());

    if (intent === 'save') {
      dispatch(reset(FORM_NAME));
      bypassBlockRef.current = true;
      onClose();
    } else if (intent === 'saveAndAddAnother') {
      dispatch(reset(FORM_NAME));
      setInitialSnapshot(null);
      setInitialFieldsSnapshot(null);
      hasAppliedDefaults.current = false;
      templatesAppliedRef.current = false;
      prevFingerprintRef.current = '';
      permissionsBaselineRef.current = '';
    }
  }, [actionStatus.creating, actionStatus.updating, successMessage, actionErrors.create, actionErrors.update, reduxDispatch, dispatch, onClose, isEditMode, requestErrorScroll]);

  useEffect(() => {
    return () => {
      reduxDispatch(clearSuccessMessage());
      reduxDispatch(clearUserDetail());
    };
  }, [reduxDispatch]);

  // Initialize add-mode defaults for fields whose UI has a default selection
  // that never fires onChange to redux-form:
  //   - temp_user: the "No" option renders as browser default but never dispatches
  //   - role: `selectedRole` state defaults to Supervisor, but the tab click is
  //           what dispatches `role` into redux-form. Without a click, the payload
  //           builder falls back to 'unsure' (see UserService.buildCreateUserPayload).
  useEffect(() => {
    if (isEditMode) return;
    if (formValuesRef.current?.temp_user === undefined) {
      dispatch(change(FORM_NAME, 'temp_user', false));
    }
    if (formValuesRef.current?.role === undefined) {
      dispatch(change(FORM_NAME, 'role', ROLES.SUPERVISOR));
    }
    // Run once on mount for add mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditMode && userId) {
      bypassBlockRef.current = false;
    }
  }, [isEditMode, userId]);

  useEffect(() => {
    if (
      isEditMode &&
      userId &&
      String(userId) !== String(userDetail?.id ?? '') &&
      !bypassBlockRef.current
    ) {
      reduxDispatch(fetchUserDetailRequest({ userId }));
    }
  }, [isEditMode, userId, userDetail, reduxDispatch]);

  // Pre-fill form from userDetail
  useEffect(() => {
    if (isEditMode && userDetail) {
      // Remember the fetched snapshot so we can restore it when the user
      // switches back to the originally saved role.
      const normalizedRole = resolveRoleCaseInsensitive(userDetail.role);
      savedUserDetailRef.current = { ...userDetail, role: normalizedRole };
      // Use initialize to seed both `initial` and `values` in one shot,
      // so redux-form dirty tracking starts from the fetched user's state.
      dispatch(initialize(FORM_NAME, { ...userDetail, role: normalizedRole }));
      if (normalizedRole) {
        setSelectedRole(normalizedRole);
      }
      const existingErrors = validateEditUserForm(userDetail);
      const fieldsToTouch = Object.keys(existingErrors).filter((key) => key !== '_error');
      if (fieldsToTouch.length > 0) {
        dispatch(touch(FORM_NAME, ...fieldsToTouch));
      }
    }
  }, [isEditMode, userDetail, dispatch]);

  // Resolve the saved supervisor by id so it can be preselected even if it
  // is not part of the currently loaded supervisors page.
  useEffect(() => {
    if (!isEditMode) return;
    const supervisorId = userDetail?.supervisor_id;
    if (!supervisorId) return;
    // Skip if already resolved or already in the loaded list.
    if (resolvedSupervisor && resolvedSupervisor.Value === supervisorId) return;
    if (supervisorsList.some((u) => u.id === supervisorId)) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetchUserApi({ userId: supervisorId });
        if (cancelled || !response) return;
        const u = response.user ?? response.User ?? response.data ?? response;
        if (!u) return;
        const firstName = u.first_name ?? u.FirstName ?? '';
        const lastName = u.last_name ?? u.LastName ?? '';
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName =
          fullName ||
          u.user_name ||
          u.UserName ||
          u.email ||
          u.Email ||
          `User #${supervisorId}`;
        setResolvedSupervisor({ Value: supervisorId, DisplayName: displayName });
      } catch (e) {
        console.error('[AddEditUser] Failed to resolve supervisor:', e);
      }
    })();

    return () => { cancelled = true; };
  }, [isEditMode, userDetail, supervisorsList, resolvedSupervisor]);

  // Build filtered permission tree
  useEffect(() => {
    const fullPermissions = getAllPermissions(currentUser, templates);
    const permissions = filterPermissionTree(fullPermissions, isModuleEnabled);
    setFilteredPermissions(permissions);
    hiddenPermissionKeysRef.current = getHiddenPermissionKeySet(fullPermissions, permissions);
  }, [currentUser, templates]);

  // Permission engine
  const allTreeKeys = getAllPermissionKeysFromTree(filteredPermissions);

  const availablePermissionKeySet = useMemo(
    () => getAvailablePermissionKeySet(filteredPermissions),
    [filteredPermissions],
  );
  const domainResolvedRules = useMemo(
    () => resolveRules(RULES, availablePermissionKeySet, domain),
    [availablePermissionKeySet, domain],
  );
  const resolvedRules = useMemo(
    () => applyRoleRuleOverrides(domainResolvedRules, selectedRole),
    [domainResolvedRules, selectedRole],
  );
  const resolvedRolePresets = useMemo(
    () => resolveRolePresets(ROLE_PRESETS, availablePermissionKeySet),
    [availablePermissionKeySet],
  );

  const currentFingerprint = useMemo(() => {
    if (!formValues || allTreeKeys.size === 0) return "";
    return Array.from(allTreeKeys)
      .map((k) => (formValues[k] ? "1" : "0"))
      .join("");
  }, [formValues, allTreeKeys]);


  useEffect(() => {
    if (isEditMode || filteredPermissions.length === 0) return;

    if (!hasAppliedDefaults.current) {
      roleChangeRef.current = selectedRole;
      hasAppliedDefaults.current = true;
      if (templates && templates.length > 0) {
        templatesAppliedRef.current = true;
      }
      return;
    }

    if (!templatesAppliedRef.current && templates && templates.length > 0) {
      templatesAppliedRef.current = true;
      roleChangeRef.current = selectedRole;
    }
  }, [isEditMode, filteredPermissions, selectedRole, templates]);

 
  useEffect(() => {
    if (!formValues || allTreeKeys.size === 0 || isUpdatingRef.current) return;

    const isRoleChange = roleChangeRef.current !== null;
    if (!isRoleChange && currentFingerprint === prevFingerprintRef.current)
      return;

    const roleForEngine = isRoleChange ? roleChangeRef.current : selectedRole;
    if (isRoleChange) roleChangeRef.current = null;

    const { nextValues, lockedPermissions, disabledPermissions, thresholdParentStates } =
      computePermissionState({
        formValues,
        rules: resolvedRules,
        rolePresets: resolvedRolePresets,
        selectedRole: roleForEngine,
        filteredPermissions,
        isRoleChange,
        prevLockedPermissions: lockedPermissionsRef.current,
        prevThresholdParentStates: thresholdParentStatesRef.current,
      });

    thresholdParentStatesRef.current = thresholdParentStates;

    const nextFingerprint = Array.from(allTreeKeys)
      .map((k) => (nextValues[k] ? "1" : "0"))
      .join("");
    const locksChanged =
      !setsAreEqual(lockedPermissions, lockedPermissionsRef.current) ||
      !setsAreEqual(disabledPermissions, disabledPermissionsRef.current);

    if (nextFingerprint === currentFingerprint && !isRoleChange) {
      prevFingerprintRef.current = currentFingerprint;
      if (locksChanged) {
        lockedPermissionsRef.current = lockedPermissions;
        disabledPermissionsRef.current = disabledPermissions;
        setDerivedState({ lockedPermissions, disabledPermissions });
      }
      return;
    }

    isUpdatingRef.current = true;
    Object.entries(nextValues).forEach(([key, value]) => {
      if (!!formValues[key] !== value) {
        if (['can_create_workkard', 'can_create_activity', 'can_reopen_activity', 'can_close_activity'].includes(key)) {
          console.log('[DEBUG] engine dispatching change >>', key, value, { isRoleChange, roleForEngine });
        }
        dispatch(change(FORM_NAME, key, value));
      }
    });

    lockedPermissionsRef.current = lockedPermissions;
    disabledPermissionsRef.current = disabledPermissions;
    setDerivedState({ lockedPermissions, disabledPermissions });
    prevFingerprintRef.current = nextFingerprint;

    // On role change, the cascade result becomes the new permissions baseline.
    // Subsequent manual checkbox clicks will diverge from this baseline and mark the form dirty.
    if (isRoleChange) {
      permissionsBaselineRef.current = nextFingerprint;
    }

    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, [currentFingerprint, selectedRole, filteredPermissions, resolvedRules, resolvedRolePresets, dispatch]);

  // ── Capture form baseline for dirty tracking ──
  useEffect(() => {
    if (initialSnapshot !== null) return;
    if (isEditMode && !userDetail) return;

    const capture = () => {
      const current = formValuesRef.current;
      setInitialSnapshot(normalizeFormValues(current));
      setInitialFieldsSnapshot(normalizeFormValues(current, { excludePermissions: true }));
      permissionsBaselineRef.current = Array.from(allTreeKeys)
        .map((k) => (current?.[k] ? "1" : "0"))
        .join("");
    };

    if (isEditMode) {
      const id = setTimeout(capture, 0);
      return () => clearTimeout(id);
    }
    capture();
  }, [initialSnapshot, isEditMode, userDetail]);

  // ── Dirty tracking + canSubmit ──
  const hasUserChanges = useMemo(() => {
    if (initialSnapshot === null) return false;

    if (!isEditMode) {
      // Add mode:
      //   - Field changes (non-permission, non-role) → dirty
      //   - Permission changes diverging from the role-cascade baseline → dirty
      //   - Role switch alone → NOT dirty (cascade updates baseline in sync)
      const fieldsDirty =
        normalizeFormValues(formValues, { excludePermissions: true }) !== initialFieldsSnapshot;
      const permissionsDirty = currentFingerprint !== permissionsBaselineRef.current;
      return fieldsDirty || permissionsDirty;
    }

    // Edit mode: any change (including role switch or permission toggle) counts as dirty
    return normalizeFormValues(formValues) !== initialSnapshot;
  }, [formValues, initialSnapshot, initialFieldsSnapshot, currentFingerprint, isEditMode]);

  const hasUserChangesRef = useRef(hasUserChanges);
  hasUserChangesRef.current = hasUserChanges;

  const blocker = useBlocker(
    useCallback(
      () => !bypassBlockRef.current && hasUserChangesRef.current,
      [],
    ),
  );
  const showDiscardConfirm = blocker.state === 'blocked';

  // Username-availability gate:
  //  - Add mode: require a successful availability check (available === true)
  //  - Edit mode: only gate when the username was actually changed from the original
  const usernameChanged =
    isEditMode && userDetail && formValues.username && formValues.username !== userDetail.username;
  const usernameCheckRequired = !isEditMode || usernameChanged;
  const usernameBlocked =
    usernameCheckRequired &&
    (usernameChecking || !!usernameError || usernameAvailable === false || usernameAvailable === null);

  // Redux-form's `invalid` prop only reflects sync errors for fields registered
  // via <Field>. Some inputs in this form (e.g. `job_role`) are custom dropdowns
  // that dispatch `change()` directly without a Field wrapper, so their errors
  // never reach `invalid`. Run the validator ourselves against formValues so
  // every required field participates in the gate.
  const blockingErrors = useMemo(() => {
    const validator = isEditMode ? validateEditUserForm : validateAddUserForm;
    const errors = validator(formValues);
    if (isEditMode && userDetail) {
      Object.keys(errors).forEach((key) => {
        if (
          key.startsWith(VALIDATION_FIELD_PREFIXES.LICENSE_EXPIRATION) &&
          formValues[key] === userDetail[key]
        ) {
          delete errors[key];
        }
      });
    }

    return errors;
  }, [formValues, isEditMode, userDetail]);

  const hasValidationErrors = Object.keys(blockingErrors).length > 0;

  const errorFieldKeys = useMemo(
    () => Array.from(new Set([
      ...Object.keys(formSyncErrors),
      ...Object.keys(blockingErrors),
      ...Object.keys(formSubmitErrors),
    ])).filter((key) => key !== '_error'),
    [formSyncErrors, blockingErrors, formSubmitErrors],
  );
  errorKeysRef.current = errorFieldKeys;

  useEffect(() => {
    if (errorScrollTick === 0) return undefined;
    const id = setTimeout(() => scrollToFirstError(rootRef.current, errorKeysRef.current), 60);
    return () => clearTimeout(id);
  }, [errorScrollTick]);

  const isSaving = actionStatus.creating || actionStatus.updating;
  const isBusy = activeAction !== null || isSaving;
  const isBlocked = (action) => isBusy && activeAction !== action;

  // ── Unsaved changes warning ──
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUserChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUserChanges]);

  // ── Role change handler ──
  const handleRoleChange = useCallback(
    (roleValue) => {
      // Edit mode: if the user returns to the originally saved role, restore
      // the exact permissions/templates that were fetched from the server
      // instead of applying the role preset. This preserves the user's
      // previously saved configuration.
      const saved = savedUserDetailRef.current;
      if (isEditMode && saved && saved.role === roleValue) {
        setSelectedRole(roleValue);
        // Skip role preset for this cycle by clearing any pending role change.
        roleChangeRef.current = null;
        Array.from(allTreeKeys).forEach((key) => {
          dispatch(change(FORM_NAME, key, !!saved[key]));
        });
        return;
      }
      roleChangeRef.current = roleValue;
      setSelectedRole(roleValue);
      dispatch(change(FORM_NAME, "role", roleValue));
    },
    [dispatch, isEditMode, allTreeKeys],
  );

  const handleClose = useCallback(() => {
    setActiveAction('cancel');
    if (!hasUserChangesRef.current) {
      dispatch(reset(FORM_NAME));
      bypassBlockRef.current = true;
    }
    onClose();
  }, [dispatch, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    dispatch(reset(FORM_NAME));
    bypassBlockRef.current = true;
    blocker.proceed?.();
  }, [dispatch, blocker]);

  const handleCancelDiscard = useCallback(() => {
    setActiveAction(null);
    blocker.reset?.();
  }, [blocker]);

  // ── Permission field change (cascade click) ──
  const applyChanges = useCallback(
    (changes) => {
      Object.entries(changes).forEach(([key, value]) => {
        dispatch(change(FORM_NAME, key, value));
      });
    },
    [dispatch],
  );
  const sanitizeFormValues = useCallback(
    (values) => {
      const hidden = hiddenPermissionKeysRef.current;
      if (!hidden || hidden.size === 0) return values;
      const sanitized = { ...values };
      hidden.forEach((key) => {
        if (sanitized[key]) sanitized[key] = false;
      });
      return sanitized;
    },
    [],
  );

  // ── Form submission ──
  const guardSubmit = () => {
    const errorKeys = Object.keys(blockingErrors);
    if (errorKeys.length > 0 || usernameBlocked) {
      dispatch(touch(FORM_NAME, ...errorKeys, 'username'));
      requestErrorScroll();
      return false;
    }
    return true;
  };

  const submitWith = (submitFn) => (event) => {
    if (isBusy) return undefined;
    const result = handleSubmit(submitFn)(event);
    const isInvalid = result && typeof result === 'object' && typeof result.then !== 'function' && Object.keys(result).length > 0;
    if (isInvalid) {
      dispatch(touch(FORM_NAME, ...Object.keys(result).filter((key) => key !== '_error')));
      requestErrorScroll();
    }
    return result;
  };

  const onSubmit = () => {
    if (!guardSubmit()) return;
    submitIntentRef.current = 'save';
    setActiveAction('save');
    const values = sanitizeFormValues(formValuesRef.current);
    if (isEditMode) {
      const payload = buildUpdateUserPayload(userId, values, currentUser, templates);
      reduxDispatch(updateUserRequest(payload));
    } else {
      const payload = buildCreateUserPayload(values, currentUser, templates);
      reduxDispatch(createUserRequest(payload));
    }
  };

  const onSubmitAndAddAnother = (values) => {
    if (!guardSubmit()) return;
    submitIntentRef.current = 'saveAndAddAnother';
    setActiveAction('saveAndAddAnother');
    const payload = buildCreateUserPayload(sanitizeFormValues(formValuesRef.current), currentUser, templates);
    reduxDispatch(createUserRequest(payload));
  };

  const isLoading = currentUserLoading || (isEditMode && userDetailLoading);

  return (
    <div className="org_access" ref={rootRef}>
      <div className="colFlex admin__header">
        <div className="rowFlex mb">
          <h1>
            <a href="javascript:void(0)" onClick={handleClose}>
              Users
            </a>{" "}
            / {isEditMode ? 'Edit User' : 'Add User'}
          </h1>
        </div>
      </div>

      <div className="add-user">
        <BasicInfo
          formName={FORM_NAME}
          isEditMode={isEditMode}
          dispatch={dispatch}
          supervisorOptions={supervisorOptions}
          onSupervisorScrollEnd={handleSupervisorScrollEnd}
          onSupervisorSearch={handleSupervisorSearch}
          supervisorsLoading={supervisorsLoading}
          supervisorError={blockingErrors.supervisor_id}
        />

        {(formSyncErrors._error) && submitFailed && (
          <div className="errorMsg" data-test="permissions-form-error">
            <span>{formSyncErrors._error}</span>
          </div>
        )}

        <PermissionSection
          selectedRole={selectedRole}
          onRoleChange={handleRoleChange}
          filteredPermissions={filteredPermissions}
          derivedState={derivedState}
          applyChanges={applyChanges}
          rules={resolvedRules}
          formValues={formValues}
        />
      </div>

      <div className="modalFooter rowFlex mt__1">
        <button
          type="button"
          className={`btn-cc__primary${isBlocked('save') ? ' disabled' : ''}`}
          disabled={isBlocked('save')}
          onClick={submitWith(onSubmit)}
        >
          {activeAction === 'save' ? <LoaderComponent /> : "Save"}
        </button>
        {!isEditMode && (
          <button
            type="button"
            className={`btn-cc__primary${isBlocked('saveAndAddAnother') ? ' disabled' : ''}`}
            disabled={isBlocked('saveAndAddAnother')}
            onClick={submitWith(onSubmitAndAddAnother)}
          >
            {activeAction === 'saveAndAddAnother' ? <LoaderComponent /> : "Save and add another user"}
          </button>
        )}
        <button
          className={`btn-cc__link${isBlocked('cancel') ? ' disabled' : ''}`}
          disabled={isBlocked('cancel')}
          onClick={isBusy ? undefined : handleClose}
        >
          {activeAction === 'cancel' ? <LoaderComponent /> : "Cancel"}
        </button>
      </div>

      {/* Discard changes confirmation */}
      <DialogueBox
        overlayClassName="modal"
        contentClassName="modalContent animated faster fadeInUpx2 small"
        shouldCloseOnOverlayClick={false}
        isOpen={showDiscardConfirm}
        onRequestClose={handleCancelDiscard}
      >
        <ConfirmationPrompt
          message="All changes will be lost if you continue. Do you want to proceed?"
          onConfirmClick={handleConfirmDiscard}
          onCancelClick={handleCancelDiscard}
          confirmationText="Yes, Discard"
          cancelText="No, Go Back"
          class="modalBody"
          bothButton={true}
          footerClass="modalFooter"
        />
      </DialogueBox>
    </div>
  );
};

const ADD_MODE_INITIAL_VALUES = {
  temp_user: false,
  role: ROLES.SUPERVISOR,
};

const AddUserFormAdd = reduxForm({
  form: FORM_NAME,
  validate: validateAddUserForm,
  enableReinitialize: true,
  touchOnBlur: true,
  touchOnChange: false,
  initialValues: ADD_MODE_INITIAL_VALUES,
})(AddUser);

const AddUserFormEdit = reduxForm({
  form: FORM_NAME,
  validate: validateEditUserForm,
  enableReinitialize: true,
  touchOnBlur: true,
  touchOnChange: false,
})(AddUser);

const AddEditUser = (props) => {
  if (props.isEditMode) {
    return <AddUserFormEdit {...props} />;
  }
  return <AddUserFormAdd {...props} />;
};

export default AddEditUser;
